'use client';

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsightsInstance: ApplicationInsights | null = null;
let initializationPromise: Promise<void> | null = null;

interface ExceptionTelemetryItem {
  baseData?: {
    exceptions?: Array<{
      message?: string;
    }>;
  };
  baseType?: string;
}

// These generic cross-origin script errors usually lack actionable stack traces,
// so suppressing them reduces telemetry noise without hiding useful diagnostics.
const SCRIPT_ERROR_SUPPRESSION_LIST = new Set([
  'Script error.',
  'ErrorEvent: Script error.',
]);

const shouldSuppressTelemetryItem = (telemetryItem: ExceptionTelemetryItem) => {
  if (telemetryItem.baseType !== 'ExceptionData') return false;

  const baseData = telemetryItem.baseData as
    | {
        exceptions?: Array<{
          message?: string;
        }>;
      }
    | undefined;

  const exceptions = baseData?.exceptions ?? [];

  if (exceptions.length === 0) return false;

  return exceptions.every(
    exception =>
      typeof exception.message === 'string' &&
      SCRIPT_ERROR_SUPPRESSION_LIST.has(exception.message),
  );
};

export async function createClientTelemetry() {
  if (appInsightsInstance) return;

  if (initializationPromise) return initializationPromise;

  initializationPromise = (async () => {
    const connectionString =
      process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;

    if (!connectionString) return;

    const { ApplicationInsights } = await import(
      '@microsoft/applicationinsights-web'
    );

    appInsightsInstance = new ApplicationInsights({
      config: {
        connectionString,
        disablePageUnloadEvents: ['unload'],
      },
    });

    appInsightsInstance.addTelemetryInitializer(telemetryItem => {
      if (shouldSuppressTelemetryItem(telemetryItem as ExceptionTelemetryItem))
        return false;

      return true;
    });

    appInsightsInstance.loadAppInsights();
    appInsightsInstance.trackPageView();
  })();

  return initializationPromise;
}

export async function trackClientMetric(
  name: string,
  value: number,
  properties: Record<string, string>,
) {
  // Early metrics like TTFB can arrive before the SDK has finished loading
  await createClientTelemetry();

  if (!appInsightsInstance) return;

  appInsightsInstance.trackMetric({ average: value, name }, properties);

  // Metrics reported while the page is being hidden (CLS, INP, LCP) would
  // otherwise sit in the buffer and be lost if the page is closed
  if (document.visibilityState === 'hidden') {
    appInsightsInstance.onunloadFlush();
  }
}
